import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Scales, Signature, FloppyDisk, FilePdf } from '@phosphor-icons/react';
import { useLanguage } from '@/contexts/LanguageContext';
import BackButton from '@/components/BackButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

const PageShell = ({ direction, children }) => (
  <div dir={direction} className="min-h-screen bg-[#F2F0FA]">
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
      <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
        <BackButton className="text-[#655dc6]" />
      </div>
    </header>
    <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      {children}
    </main>
  </div>
);

export default function EmployeeInvestigation() {
  const { id } = useParams();
  const nav = useNavigate();
  const { language, direction } = useLanguage();
  const isAr = language === 'ar';
  const T = (a, e) => (isAr ? a : e);

  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(null);
  const [text, setText] = useState('');
  const [sigName, setSigName] = useState('');
  const [saving, setSaving] = useState(false);

  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  useEffect(() => {
    axios.get(`${API}/investigations/mine`, { withCredentials: true })
      .then(r => {
        setItems(r.data);
        if (id) {
          const target = r.data.find(x => x.id === id);
          if (target) setCurrent(target);
        } else if (r.data.length === 1) {
          setCurrent(r.data[0]);
        }
        // If multiple and no id in URL → show list view (current stays null)
      });
  }, [id]);

  // ─── Signature pad ───────────────────────────────────────────────────
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.strokeStyle = '#000';
    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const t = e.touches?.[0] || e;
      // Scale from CSS pixels → internal canvas coordinates
      const scaleX = c.width / r.width;
      const scaleY = c.height / r.height;
      return {
        x: (t.clientX - r.left) * scaleX,
        y: (t.clientY - r.top) * scaleY,
      };
    };
    const start = (e) => { e.preventDefault(); drawingRef.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawingRef.current) return; e.preventDefault(); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
    const end = () => { drawingRef.current = false; };
    c.addEventListener('mousedown', start); c.addEventListener('mousemove', move);
    c.addEventListener('mouseup', end);     c.addEventListener('mouseleave', end);
    c.addEventListener('touchstart', start, { passive: false });
    c.addEventListener('touchmove', move,   { passive: false });
    c.addEventListener('touchend', end);
    return () => {
      c.removeEventListener('mousedown', start); c.removeEventListener('mousemove', move);
      c.removeEventListener('mouseup', end);     c.removeEventListener('mouseleave', end);
      c.removeEventListener('touchstart', start); c.removeEventListener('touchmove', move);
      c.removeEventListener('touchend', end);
    };
  }, [current]);

  const clearSig = () => {
    const c = canvasRef.current; if (!c) return;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
  };

  const isCanvasBlank = () => {
    const c = canvasRef.current;
    if (!c) return true;
    const px = c.getContext('2d').getImageData(0, 0, c.width, c.height).data;
    for (let i = 3; i < px.length; i += 4) if (px[i] !== 0) return false;
    return true;
  };

  const submit = async () => {
    if (!text.trim()) return toast.error(T('اكتب أقوالك أولاً', 'Write your statement first'));
    if (!sigName.trim()) return toast.error(T('اكتب اسمك للتوقيع', 'Enter your name'));
    if (isCanvasBlank()) return toast.error(T('يجب التوقيع رقمياً', 'Digital signature required'));
    setSaving(true);
    try {
      const png = canvasRef.current.toDataURL('image/png');
      await axios.post(`${API}/investigations/${current.id}/statement`, {
        statement_text: text, signature_png: png, signature_name: sigName,
      }, { withCredentials: true });
      toast.success(T('تم تسجيل أقوالك بنجاح', 'Statement recorded'));
      nav('/home');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const submitCommitteeSignature = async () => {
    if (!sigName.trim()) return toast.error(T('اكتب اسمك للتوقيع', 'Enter your name'));
    if (isCanvasBlank()) return toast.error(T('يجب التوقيع رقمياً', 'Digital signature required'));
    setSaving(true);
    try {
      const png = canvasRef.current.toDataURL('image/png');
      await axios.post(`${API}/investigations/${current.id}/committee-sign`, {
        signature_png: png, signature_name: sigName,
      }, { withCredentials: true });
      toast.success(T('تم توقيعك على القرار بنجاح', 'Signature recorded'));
      nav('/home');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (!current) {
    if (items.length === 0) {
      return (
        <PageShell direction={direction}>
          <Card className="border border-[#C4BFF0]">
            <CardHeader><CardTitle className="text-[#655dc6] flex items-center gap-2"><Scales size={22} weight="duotone"/> {T('تحقيقاتي', 'My Investigations')}</CardTitle></CardHeader>
            <CardContent>
              <p className="text-center text-[#655dc6]/60 py-6">{T('لا يوجد أي تحقيق مفتوح بحقك.', 'You have no active investigation.')}</p>
            </CardContent>
          </Card>
        </PageShell>
      );
    }
    // Multiple investigations — show picker list (uses full main width)
    return (
      <PageShell direction={direction}>
        <Card className="border border-[#C4BFF0]">
          <CardHeader>
            <CardTitle className="text-[#655dc6] flex items-center gap-2">
              <Scales size={22} weight="duotone"/>
              {T('تحقيقاتي', 'My Investigations')}
              <Badge variant="outline">{items.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {items.map(inv => {
              const isCommittee = inv.role === 'committee';
              const pendingFu = (inv.my_follow_ups || []).some(fu => !fu.answered_at);
              const needsAction = isCommittee
                ? (inv.decision_saved && !inv.my_committee_signed)
                : (!inv.my_statement_submitted || pendingFu);
              return (
              <button
                key={inv.id}
                onClick={() => setCurrent(inv)}
                data-testid={`my-inv-${inv.id}`}
                className="w-full text-start p-4 rounded border border-[#C4BFF0] hover:border-[#655dc6] hover:bg-[#F2F0FA] transition group"
              >
                <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-[#655dc6]/70">{inv.session_number}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {isCommittee
                        ? T(`عضو لجنة${inv.committee_role ? ` (${inv.committee_role})` : ''}`, `Committee${inv.committee_role ? ` (${inv.committee_role})` : ''}`)
                        : T('موظف محل التحقيق', 'Subject Employee')}
                    </Badge>
                  </div>
                  {needsAction
                    ? <Badge className="bg-amber-100 text-amber-800 text-[10px]">{T('يتطلب إجراءك', 'Action required')}</Badge>
                    : <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{T('مكتمل', 'Complete')}</Badge>}
                </div>
                <p className="text-sm text-[#655dc6] font-medium line-clamp-2 break-words" style={{ overflowWrap: 'anywhere' }}>{inv.subject}</p>
                {!isCommittee && (
                  <p className="text-[11px] text-[#655dc6]/60 mt-1">
                    {T('المهلة: ', 'Deadline: ')}<span dir="ltr">{inv.statement_deadline}</span>
                  </p>
                )}
              </button>
              );
            })}
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  // ── COMMITTEE MEMBER VIEW ──────────────────────────────────────────
  if (current.role === 'committee') {
    if (!current.decision_saved) {
      // Admin has not saved a decision yet — nothing for committee to do
      return (
        <PageShell direction={direction}>
          <Card className="border border-[#C4BFF0]">
            <CardHeader>
              <CardTitle className="text-[#655dc6] flex items-center gap-2">
                <Scales size={22} weight="duotone"/> {current.session_number}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Badge className="bg-[#655dc6] text-white">{T(`دورك: ${current.committee_role || 'عضو لجنة'}`, `Role: ${current.committee_role || 'Committee'}`)}</Badge>
              <p className="text-sm text-[#655dc6]"><strong>{T('الموضوع: ', 'Subject: ')}</strong>{current.subject}</p>
              <div className="bg-[#F2F0FA] border-r-4 border-[#655dc6] p-3 rounded text-sm text-[#655dc6]">
                {T('لم يتم تسجيل قرار التحقيق من قِبل مدير النظام بعد. ستتلقى إشعاراً حين يصبح جاهزاً للتوقيع.',
                   'The investigation decision has not been saved by the admin yet. You will be notified once it is ready for your signature.')}
              </div>
            </CardContent>
          </Card>
        </PageShell>
      );
    }
    if (current.my_committee_signed) {
      return (
        <PageShell direction={direction}>
          <Card className="border border-[#C4BFF0]">
            <CardHeader>
              <CardTitle className="text-[#655dc6] flex items-center gap-2">
                <Scales size={22} weight="duotone"/> {current.session_number}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Badge className="bg-emerald-100 text-emerald-700">{T('تم توقيعك بنجاح ✓', 'Signature recorded ✓')}</Badge>
              <p className="text-sm text-[#655dc6]"><strong>{T('الموضوع: ', 'Subject: ')}</strong>{current.subject}</p>
              <div className="mt-4 p-3 border-2 border-[#655dc6] rounded">
                <p className="font-semibold text-[#655dc6] mb-1">{T('القرار الرسمي', 'Official Decision')}</p>
                <p className="text-sm text-[#655dc6]/80 whitespace-pre-wrap">{current.decision_text}</p>
              </div>
              {current.all_committee_signed ? (
                <Button variant="outline" size="sm"
                        onClick={() => window.open(`${API}/investigations/${current.id}/pdf`, '_blank')}
                        className="text-[#655dc6]"
                        data-testid="download-inv-pdf-committee">
                  <FilePdf size={16} className="me-1" /> {T('تحميل نسخة PDF', 'Download PDF')}
                </Button>
              ) : (
                <div
                  className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2"
                  data-testid="committee-pdf-locked-note"
                >
                  <Scales size={14} className="mt-0.5 shrink-0" />
                  <span>
                    {T(
                      'ستُتاح نسخة PDF بمجرد اكتمال توقيعات جميع أعضاء اللجنة.',
                      'The PDF will become available once every committee member has signed.',
                    )}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </PageShell>
      );
    }
    // Decision issued + signature pending → show signature form
    return (
      <PageShell direction={direction}>
        <Card className="border border-[#C4BFF0]">
          <CardHeader>
            <CardTitle className="text-[#655dc6] flex items-center gap-2">
              <Scales size={24} weight="duotone"/>
              {T(`توقيع قرار التحقيق #${current.session_number}`, `Sign Investigation Decision #${current.session_number}`)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Badge className="bg-[#655dc6] text-white">{T(`صفتك: ${current.committee_role || 'عضو لجنة'}`, `Your role: ${current.committee_role || 'Committee'}`)}</Badge>
            <div><strong className="text-sm text-[#655dc6]">{T('الموضوع: ', 'Subject: ')}</strong><span className="text-sm">{current.subject}</span></div>
            {current.findings && (
              <div>
                <Label className="text-xs text-[#655dc6]">{T('نتائج اللجنة', 'Findings')}</Label>
                <div className="bg-[#F2F0FA] p-3 rounded text-sm text-[#222] whitespace-pre-wrap">{current.findings}</div>
              </div>
            )}
            <div>
              <Label className="text-xs text-[#655dc6]">{T('نص القرار الرسمي', 'Official Decision Text')}</Label>
              <div className="bg-amber-50 border border-amber-300 p-3 rounded text-sm text-[#222] whitespace-pre-wrap">{current.decision_text}</div>
            </div>

            <div>
              <Label className="text-sm text-[#655dc6] font-semibold flex items-center gap-2">
                <Signature size={18}/> {T('توقيعك الرقمي على القرار *', 'Your Digital Signature *')}
              </Label>
              <p className="text-xs text-[#655dc6]/70 mb-2">
                {T('اكتب اسمك ثم وقّع داخل المربع للموافقة على القرار بصفتك عضو لجنة تحقيق.',
                   'Enter your name then sign to endorse the decision as a committee member.')}
              </p>
              <Input placeholder={T('الاسم الكامل', 'Full Name')} value={sigName} onChange={e => setSigName(e.target.value)} className="mb-2" data-testid="cm-sig-name"/>
              <div className="border-2 border-[#C4BFF0] rounded bg-white">
                <canvas ref={canvasRef} width={640} height={180} className="w-full touch-none cursor-crosshair" data-testid="cm-sig-canvas"/>
              </div>
              <Button variant="ghost" size="sm" onClick={clearSig} className="mt-1 text-[#655dc6]/70">{T('مسح التوقيع', 'Clear')}</Button>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => nav('/home')}>{T('إلغاء', 'Cancel')}</Button>
              <Button onClick={submitCommitteeSignature} disabled={saving} className="bg-[#655dc6] hover:bg-[#544cb0]" data-testid="cm-sign-btn">
                <FloppyDisk size={16} className="me-1" /> {saving ? T('جارٍ الحفظ...', 'Signing...') : T('تأكيد التوقيع على القرار', 'Confirm Signature')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  if (current.my_statement_submitted) {
    const followUps = current.my_follow_ups || [];
    return (
      <PageShell direction={direction}>
        <Card className="border border-[#C4BFF0]">
          <CardHeader><CardTitle className="text-[#655dc6] flex items-center gap-2"><Scales size={22}/> {current.session_number}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-[#655dc6]"><strong>{T('الموضوع: ', 'Subject: ')}</strong>{current.subject}</p>
            <Badge className="bg-emerald-100 text-emerald-700">{T('تم تسجيل أقوالك ✓', 'Statement submitted ✓')}</Badge>
            <div className="bg-[#F2F0FA] p-3 rounded text-sm text-[#655dc6] whitespace-pre-wrap">{current.my_statement_text}</div>

            {/* Follow-up questions section */}
            {followUps.length > 0 && (
              <div className="border border-[#655dc6] rounded p-3 bg-[#F5F3FF]">
                <p className="text-sm font-semibold text-[#655dc6] mb-2">
                  {T('أسئلة إضافية موجّهة إليك', 'Follow-up questions directed to you')}
                </p>
                <div className="space-y-3">
                  {followUps.map((fu) => (
                    <FollowUpItem
                      key={fu.index}
                      invId={current.id}
                      fu={fu}
                      T={T}
                      onAnswered={() => {
                        // Re-fetch this investigation from /mine to refresh answers
                        axios.get(`${API}/investigations/mine`, { withCredentials: true })
                          .then(r => {
                            setItems(r.data);
                            const upd = r.data.find(x => x.id === current.id);
                            if (upd) setCurrent(upd);
                          }).catch(() => {});
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {current.result_sent_at && (
              <div className="mt-4 p-3 border-2 border-[#655dc6] rounded">
                <p className="font-semibold text-[#655dc6] mb-1">{T('نتيجة التحقيق', 'Investigation Result')}</p>
                <p className="text-sm text-[#655dc6]/80"><strong>{T('القرار: ', 'Decision: ')}</strong>{current.recommended_action}</p>
                <p className="text-sm text-[#655dc6]/80 whitespace-pre-wrap mt-2">{current.decision_text}</p>
              </div>
            )}
            {current.result_sent_at ? (
              <div className="pt-2">
                <Button variant="outline" size="sm" onClick={() => window.open(`${API}/investigations/${current.id}/pdf`, '_blank')} className="text-[#655dc6]" data-testid="download-inv-pdf">
                  <FilePdf size={16} className="me-1" /> {T('تحميل نسخة PDF', 'Download PDF')}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-[#655dc6]/60 italic pt-2">
                {T('سيصبح تحميل نسخة PDF متاحاً بعد إرسال القرار الرسمي.',
                   'PDF download will be available once the official decision is sent.')}
              </p>
            )}
          </CardContent>
        </Card>
      </PageShell>
    );
  }

  return (
    <PageShell direction={direction}>
      <Card className="border border-[#C4BFF0]">
        <CardHeader>
          <CardTitle className="text-[#655dc6] flex items-center gap-2">
            <Scales size={24} weight="duotone" />
            {T(`تحقيق داخلي #${current.session_number}`, `Investigation #${current.session_number}`)}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-amber-50 border border-amber-300 rounded p-3 text-sm text-amber-900">
            <strong>{T('المهلة: ', 'Deadline: ')}</strong>
            <span dir="ltr">{current.statement_deadline}</span>
            <span className="ms-2">— {T('3 أيام عمل بحد أقصى', '3 working days max')}</span>
          </div>
          <div><Label className="text-sm text-[#655dc6]"><strong>{T('الموضوع: ', 'Subject: ')}</strong>{current.subject}</Label></div>

          {current.my_questions && (
            <div className="bg-[#F5F3FF] border-r-4 border-[#655dc6] rounded p-3">
              <p className="text-xs font-semibold text-[#655dc6] mb-1">
                {T('أسئلة موجّهة إليك من اللجنة', 'Committee questions directed to you')}
              </p>
              <p className="text-sm text-[#222] whitespace-pre-wrap" style={{ overflowWrap: 'anywhere' }}>
                {current.my_questions}
              </p>
            </div>
          )}

          <div>
            <Label className="text-sm text-[#655dc6] font-semibold">{T('أقوالك بالتفصيل *', 'Your Statement *')}</Label>
            <Textarea rows={8} value={text} onChange={e => setText(e.target.value)}
                      placeholder={T('اكتب أقوالك هنا بالتفصيل...', 'Write your statement here in detail...')}
                      className="mt-1" data-testid="statement-text"/>
          </div>

          <div>
            <Label className="text-sm text-[#655dc6] font-semibold flex items-center gap-2">
              <Signature size={18} /> {T('التوقيع الرقمي *', 'Digital Signature *')}
            </Label>
            <p className="text-xs text-[#655dc6]/70 mb-2">{T('اكتب اسمك ثم وقّع بالماوس أو باللمس داخل المربع.', 'Type your name then sign with mouse or touch.')}</p>
            <Input placeholder={T('الاسم الكامل', 'Full Name')} value={sigName} onChange={e => setSigName(e.target.value)} className="mb-2" data-testid="sig-name"/>
            <div className="border-2 border-[#C4BFF0] rounded bg-white">
              <canvas ref={canvasRef} width={640} height={180} className="w-full touch-none cursor-crosshair" data-testid="sig-canvas"/>
            </div>
            <Button variant="ghost" size="sm" onClick={clearSig} className="mt-1 text-[#655dc6]/70" data-testid="clear-sig-btn">{T('مسح التوقيع', 'Clear signature')}</Button>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => nav('/home')}>{T('إلغاء', 'Cancel')}</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#655dc6] hover:bg-[#544cb0]" data-testid="submit-statement-btn">
              <FloppyDisk size={16} className="me-1" /> {saving ? T('جارٍ الإرسال...', 'Submitting...') : T('تسجيل الأقوال والتوقيع', 'Submit & Sign')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </PageShell>
  );
}

function FollowUpItem({ invId, fu, T, onAnswered }) {
  const [answer, setAnswer] = useState('');
  const [sending, setSending] = useState(false);
  const answered = !!fu.answered_at && !!fu.answer;

  const submit = async () => {
    if (!answer.trim()) return toast.error(T('اكتب الإجابة أولاً', 'Enter your answer first'));
    setSending(true);
    try {
      await axios.post(
        `${API}/investigations/${invId}/followup-answer/${fu.index}`,
        { answer: answer.trim() },
        { withCredentials: true },
      );
      toast.success(T('تم إرسال الإجابة', 'Answer submitted'));
      onAnswered && onAnswered();
    } catch (e) {
      toast.error(e.response?.data?.detail || T('فشل الإرسال', 'Failed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-[#C4BFF0] rounded p-2 bg-white" data-testid={`fu-item-${fu.index}`}>
      <p className="text-xs font-semibold text-[#655dc6]">
        {T('السؤال:', 'Question:')} <span className="font-normal text-[#222]">{fu.question}</span>
      </p>
      {answered ? (
        <div className="mt-2 bg-[#F2F0FA] rounded p-2">
          <p className="text-[11px] text-[#065F46] font-semibold">{T('إجابتك ✓', 'Your answer ✓')}</p>
          <p className="text-xs text-[#222] whitespace-pre-wrap" style={{ overflowWrap: 'anywhere' }}>{fu.answer}</p>
        </div>
      ) : (
        <div className="mt-2">
          <Textarea
            rows={3}
            value={answer}
            onChange={e => setAnswer(e.target.value)}
            placeholder={T('اكتب إجابتك على هذا السؤال…', 'Write your answer here…')}
            data-testid={`fu-answer-input-${fu.index}`}
          />
          <div className="flex justify-end mt-2">
            <Button
              size="sm"
              disabled={sending || !answer.trim()}
              onClick={submit}
              className="bg-[#655dc6] hover:bg-[#544cb0] text-white"
              data-testid={`fu-answer-submit-${fu.index}`}
            >
              {sending ? T('جارٍ الإرسال…', 'Sending…') : T('إرسال الإجابة', 'Submit Answer')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
